import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField, Autocomplete } from '@mui/material';
import { FormControl, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DirectionService from '../../../services/DirectionService';
import ServiceService from '../../../services/ServiceService';
import UserService from '../../../services/UserService';
import { LoadingButton } from '@mui/lab';
import TimeSheetMissionService from '../../../services/TimeSheetMissionService';
import Message from '../../tools/Message';
import ShareIcon from '@mui/icons-material/Share';

const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        borderRadius: 18,
        boxShadow: '0 18px 40px rgba(25, 118, 210, 0.25)',
    },
    dialogTitle: {
        color: '#ffffff',
        fontWeight: 700,
        letterSpacing: 0.4,
        padding: theme.spacing(2, 3),
    },
    dialogContent: {
        // backgroundColor: '#fbfcff',
        padding: theme.spacing(2.5, 3),
    },
    dialogText: {
        color: '#1b1f24',
        fontWeight: 500,
        marginBottom: theme.spacing(2),
    },
    helperNote: {
        // background: 'linear-gradient(135deg, #eef2ff 0%, #ffffff 100%)',
        border: '1px solid #c7d2fe',
        borderRadius: 10,
        padding: theme.spacing(1.5),
        color: '#1e3a8a',
    },
    fieldWrap: {
        backgroundColor: '#ffffff',
        borderRadius: 10,
        padding: theme.spacing(1.5),
    },
    formLabel: {
        display: 'block',
        fontWeight: 600,
        color: '#294270',
        marginBottom: theme.spacing(0.5),
    },
    dialogActions: {
        padding: theme.spacing(2, 3),
        justifyContent: 'space-between',
    },
    actionButton: {
        fontWeight: 700,
        textTransform: 'none',
        borderRadius: 10,
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
}));

const TimesheetShareDialog = (props) => {
    const { open, onClose, mission_timesheet_id, mission_planning_agenda_id, mission_planning_id } = props;
    const classes = useStyles();
    const [directionOptions, setDirectionOptions] = useState([]);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [selectedDirection, setSelectedDirection] = useState(null);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({
        open: false,
        color: 'green',
        messageInfo: '',
    });
    const { open: openMessage, color, messageInfo } = message;
    function handleCloseMessage() {
        setMessage({ ...message, open: false });
    }
    useEffect(() => {
        if (!open) {
            setSelectedDirection(null);
            setSelectedService(null);
            setSelectedUser(null);
            setServiceOptions([]);
            setUserOptions([]);
            setMessage((prev) => ({ ...prev, open: false }));
            return;
        }
        DirectionService.getDirection().then((res) => {
            if (res?.status && res.data) {
                setDirectionOptions(
                    res.data.map((row) => ({
                        label: `${row.code} - ${row.name}`,
                        value: row.direction_id,
                    }))
                );
            }
        });
    }, [open]);

    const handleSelectDirection = (_event, newValue) => {
        setSelectedDirection(newValue);
        setSelectedService(null);
        setSelectedUser(null);
        setServiceOptions([]);
        setUserOptions([]);

        if (newValue?.value) {
            ServiceService.getServById(newValue.value).then((res) => {
                if (res?.data) {
                    setServiceOptions(
                        res.data.map((row) => ({
                            label: `${row.code} - ${row.name}`,
                            value: row.service_id,
                        }))
                    );
                }
            });
        }
    };

    const fetchUsers = (directionId, serviceId) => {
        UserService.getAllUserlist(directionId, serviceId).then((res) => {
            if (res?.data) {
                setUserOptions(
                    res.data.map((row) => ({
                        label: `${row.first_name ?? ''} ${row.last_name ?? ''} (${row.email ?? ''})`,
                        value: row.user_id,
                    }))
                );
            }
        });
    };

    const handleSelectService = (_event, newValue) => {
        setSelectedService(newValue);
        setSelectedUser(null);
        setUserOptions([]);
    };

    const handleSelectUser = (_event, newValue) => {
        setSelectedUser(newValue);
    };
    
    useEffect(() => {
        if (selectedDirection?.value && selectedService?.value) {
            fetchUsers(selectedDirection.value, selectedService.value);
        }
    }, [selectedDirection, selectedService]);

    const handleShareTimesheet = async (mission_timesheet_id, mission_planning_agenda_id, mission_planning_id, selectedUser) => {
        if (!selectedUser?.value) return;
        setLoading(true);
        try {
            const emailMatch = selectedUser.label.match(/\(([^)]+)\)/);
            const payload = {
                mission_planning_agenda_id: mission_planning_agenda_id,
                mission_planning_id: mission_planning_id,
                mission_timesheet_id: mission_timesheet_id,
                receiver_id: selectedUser.value,
                emails: emailMatch?.[1] ?? '',
            };
            console.log('Payload for sharing timesheet mission:', payload);
            const result = await TimeSheetMissionService.shareTimesheetMission(payload);

            if (result?.status) {
                setMessage({
                    open: true,
                    color: 'green',
                    messageInfo: 'Élaboration de la timesheet partagée avec succès.',
                });
                onClose();
            } else {
                if(result?.code === 503){
                    setMessage({
                        open: true,
                        color: 'red',
                        messageInfo: "Le partage a été effectué, mais le service d’email est indisponible. L’email de la timesheet n’a pas été envoyé.",
                    });
                    onClose();
                    return;
                }
                setMessage({
                    open: true,
                    color: 'red',
                    messageInfo: "Erreur lors du partage de l'élaboration de la timesheet.",
                });
            }
        } catch (err) {
            setMessage({
                open: true,
                color: 'red',
                messageInfo: "Erreur lors du partage de l'élaboration de la timesheet.",
            });
            console.error('Error sharing timesheet mission:', err);
        } finally {
            setLoading(false);
        }
    };

  return (
    <>
      {/* // dialog share timesheet */}
            <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.dialogPaper }}>
              <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
                <ShareIcon/> Partager l'elaboration de la timesheet
              </DialogTitle>
              <DialogContent dividers className={classes.dialogContent}>
                <DialogContentText className={classes.dialogText}>
                <Message open={openMessage} handleClose={handleCloseMessage} color={color} messageInfo={messageInfo} />
                    <Grid container spacing={2} style={{ marginBottom: '16px' }}>
                        <Grid item xs={12}>
                            <div className={classes.helperNote}>
                                Veuillez selectionner un utilisateur autre que les missionnaires avec lequel vous souhaitez partager l'elaboration de la timesheet.
                            </div>
                        </Grid>
                    <Grid item xs={12}>
                    <FormControl fullWidth className={classes.fieldWrap}>
                        <label className={classes.formLabel}>Direction</label>
                        <Autocomplete
                            id="direction"
                            size='small'
                            options={directionOptions}
                            getOptionLabel={(option) => option?.label ?? ''}
                            value={selectedDirection}
                            onChange={handleSelectDirection}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            renderInput={(params) => <TextField {...params} placeholder="Choisir une direction" />}
                            fullWidth

                        />
                    </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                    <FormControl fullWidth className={classes.fieldWrap}>
                        <label className={classes.formLabel}>Service</label>
                        <Autocomplete
                            id="service"
                            size='small'
                            options={serviceOptions}
                            getOptionLabel={(option) => option?.label ?? ''}
                            value={selectedService}
                            onChange={handleSelectService}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            renderInput={(params) => <TextField {...params} placeholder="Choisir un service" />}
                            disabled={!selectedDirection}
                            fullWidth
                        />
                    </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                    
                    <FormControl fullWidth className={classes.fieldWrap}>
                        <label className={classes.formLabel}>Utilisateur</label>
                        <Autocomplete
                            id="user"
                            size='small'
                            options={userOptions}
                            getOptionLabel={(option) => option?.label ?? ''}
                            value={selectedUser}
                            onChange={handleSelectUser}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            renderInput={(params) => <TextField {...params} placeholder="Choisir un utilisateur" />}
                            disabled={!selectedService}
                            fullWidth
                        />
                    </FormControl>
                    </Grid>
                    </Grid>
                </DialogContentText>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                 <Button onClick={() => onClose()} color="error" variant='outlined' className={classes.actionButton}>
                  Fermer
                </Button>
                <LoadingButton
                    loading={loading}
                    onClick={() => handleShareTimesheet(mission_timesheet_id, mission_planning_agenda_id, mission_planning_id, selectedUser)}
                    color="success"
                    variant='contained'
                    disabled={!selectedUser}
                    className={classes.actionButton}
                >
                  Partager
                </LoadingButton>
              </DialogActions>
            </Dialog>
        </>

    );
};
export default TimesheetShareDialog;
